import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ContractService } from '../../services/contract.service';
import { ToastrService } from 'ngx-toastr';
import { ProposeTransactionService } from '../../services/propose-transaction.service';
import { walletAddressValidator } from '../../../shared/validators/walletAddressValidator';
import { environment } from '../../../../environments/environment';
import { PercentageRangeDirective } from '../../../shared/directives/percentageRange.directive';
import { NgClass } from '@angular/common';
import { waitForTransactionReceipt } from '@wagmi/core';
import { wagmiConfig } from '../../config/wagmi-config';
import { ContractType } from '../../../shared/enums/common.enum';
import { AmountValidatorDirective } from '../../../shared/directives/amount-validator.directive';
import { WholeNumberOnlyDirective } from "../../../shared/directives/wholeNumberOnly.directive";
import { getAmountInDecimals } from '../../../shared/helpers/common.helper';
@Component({
  selector: 'app-propose-transaction',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PercentageRangeDirective,
    NgClass,
    AmountValidatorDirective
],
  templateUrl: './propose-transaction.component.html',
  styleUrl: './propose-transaction.component.scss'
})
export class ProposeTransactionComponent implements OnInit {
  proposeForm!: FormGroup;
  fb = inject(FormBuilder);
  contractService = inject(ContractService);
  toastr = inject(ToastrService);
  proposeService = inject(ProposeTransactionService);
  contractAddress: string = '';
  methodList: any[] = [];
  isProcessing = signal(false);
  contractType: any;
  selectedMethod: any = null;
  multiSigContractAddress: any;

  ngOnInit(): void {
    this.initForm();
    this.getFunctionsForSigners();
    this.proposeForm.get('method')?.valueChanges.subscribe(methodKey => {
      this.applyConditionalValidators(methodKey);
    });
  
    // Initialize validators for default or initial value
    this.applyConditionalValidators(this.proposeForm.get('method')?.value);
  }

  /**
   * Applys conditional validators
   * @param methodKey
   */
  applyConditionalValidators(methodKey: string | null): void {
    this.proposeForm.get('percentage')?.clearValidators();
    this.proposeForm.get('date')?.clearValidators();
    this.proposeForm.get('count')?.clearValidators();
    this.proposeForm.get('wallet_address')?.clearValidators();
    this.proposeForm.get('description')?.clearValidators();
    this.proposeForm.get('amount')?.clearValidators(); 

    switch (methodKey) {
      case 'updateLtafPercentage':
        this.proposeForm.get('percentage')?.setValidators([Validators.required]);
        this.proposeForm.get('description')?.setValidators([Validators.required]);
        break;
  
      case 'bulkDistribution':
        this.proposeForm.get('date')?.setValidators([Validators.required]);
        this.proposeForm.get('count')?.setValidators([Validators.required]);
        this.proposeForm.get('description')?.setValidators([Validators.required]);
        break;
        
      case 'transfer': 
        this.proposeForm.get('wallet_address')?.setValidators([Validators.required, walletAddressValidator()]);
        this.proposeForm.get('amount')?.setValidators([Validators.required]); 
        this.proposeForm.get('description')?.setValidators([Validators.required]);
        break;
        
  
      default:
        this.proposeForm.get('wallet_address')?.setValidators([Validators.required]);
        this.proposeForm.get('description')?.setValidators([Validators.required]);
        break;
    }
  
    this.proposeForm.get('percentage')?.updateValueAndValidity();
    this.proposeForm.get('date')?.updateValueAndValidity();
    this.proposeForm.get('count')?.updateValueAndValidity();
    this.proposeForm.get('wallet_address')?.updateValueAndValidity();
    this.proposeForm.get('description')?.updateValueAndValidity();
    this.proposeForm.get('amount')?.updateValueAndValidity(); 
  }

  /**
   * Inits form
   */
  initForm() {
    this.proposeForm = this.fb.group({
      method: [''],
      address: [''],
      description: [''],
      wallet_address: ['', walletAddressValidator()],
      percentage: [''],
      date: [''],
      count: [''],
      amount: [''] 
    })
  }

  /**
   * Gets functions for signers
   */
  getFunctionsForSigners() {
    this.proposeService.getsignerFunctions().subscribe({
      next: (response: any) => {
        console.log('get methods', response);
        this.multiSigContractAddress = response.data.contracts[0].address;
        this.proposeForm.patchValue({ address: response.data.contracts[0].address });
        this.contractType = response.data.contracts[0].name;
        if(this.contractType == ContractType.AOC_LOCKER){
            this.contractType = response.data.contracts[0].functions.transfer.contract;
        }
        // Convert object into array for looping
        this.methodList = Object.keys(response.data.contracts[0].functions).map((key: any) => {
          return {
            key: key, // function name
            ...response.data.contracts[0].functions[key] // function details
          };
        });

        console.log('methodList:', this.methodList);
      },
      error: (error: any) => {
        console.log('err method', error);
        
      }
    })
  }

  /**
   * Creates propose transaction
   */
  async createProposeTransaction() {
    this.isProcessing.set(true);
    try {
      let args: any[] = [];
      if (this.proposeForm.value.method === 'updateLtafPercentage') {
        const percentage = Number(this.proposeForm.value.percentage) * 100;
        args = [percentage];
      } else if (this.proposeForm.value.method === 'bulkDistribution') {
        
        args = [this.proposeForm.value.date, this.proposeForm.value.count];
      } else if (this.proposeForm.value.method === 'transfer') {
        // const amount = Number(this.proposeForm.value.amount) * 10 ** 18; 
        const amount = await getAmountInDecimals(this.proposeForm.value.amount, 18);
        args = [this.proposeForm.value.wallet_address, amount];

      } else {
        if (this.proposeForm.value.method === 'blacklistUser') {
          const isBlacklisted = await this.contractService.isBlacklisted(this.proposeForm.value.wallet_address);
          if (isBlacklisted) {
            this.toastr.error('Wallet Address already in blacklist')
            this.isProcessing.set(false);
            return;
          }
        }
        if (this.proposeForm.value.method === 'removeFromBlacklist') {
          const isBlacklisted = await this.contractService.isBlacklisted(this.proposeForm.value.wallet_address);
          if (!isBlacklisted) {
            this.toastr.error('Wallet Address not in blacklist')
            this.isProcessing.set(false);
            return;
          }
        }
        if (this.proposeForm.value.method === 'includeInRAMS') {
          const isExcludedFromRAMS = await this.contractService.isExcludedFromRAMS(this.proposeForm.value.wallet_address);
          if (!isExcludedFromRAMS) {
            this.toastr.error('Wallet Address already included from RAMS')
            this.isProcessing.set(false);
            return;
          }
        }
        if (this.proposeForm.value.method === 'excludeFromRAMS') {
          const isExcludedFromRAMS = await this.contractService.isExcludedFromRAMS(this.proposeForm.value.wallet_address);
          if (isExcludedFromRAMS) {
            this.toastr.error('Wallet Address not included in RAMS to exclude')
            this.isProcessing.set(false);
            return;
          }
        }
        if (this.proposeForm.value.method === 'includeInLTAF') {
          const isIncludedInLTAF = await this.contractService.isIncludedInLTAF(this.proposeForm.value.wallet_address);
          if (isIncludedInLTAF) {
            this.toastr.error('Wallet Address already included in LTAF')
            this.isProcessing.set(false);
            return;
          }
        }
        if (this.proposeForm.value.method === 'excludedFromLTAF') {
          const isIncludedInLTAF = await this.contractService.isIncludedInLTAF(this.proposeForm.value.wallet_address);
          if (!isIncludedInLTAF) {
            this.toastr.error('Wallet Address not included in LTAF to exclude')
            this.isProcessing.set(false);
            return;
          }
        }      
        args = [this.proposeForm.value.wallet_address];
      }
      const address = this.contractType == ContractType.AOC_TOKEN ? environment.AOC_TOKEN_CONTRACT: this.contractType === ContractType.AOC_LOCKER ? environment.AOC_TOKEN_LOCKER : environment.BULKMINT_CONTRACT;      
      const encodedData = await this.contractService.encodedDataFunction(this.contractType, this.proposeForm.value.method, args);      
      await this.contractService.proposeTransactions(this.multiSigContractAddress, address, encodedData, 0, this.proposeForm.value.description).then(async(response: any) => {
        if (response) {
          const receipt = await waitForTransactionReceipt(wagmiConfig,{
            confirmations: 1,
            hash: response as `0x${string}`, // Type assertion to ensure correct format
          });
          if (receipt) {
            this.toastr.success('Propose Transaction Successful');
            // Reset only relevant fields
            this.proposeForm.patchValue({
              method: '',
              wallet_address: '',
              percentage: '',
              description: '',
              date: '',
              count: ''
            });
            this.selectedMethod = null;
            this.isProcessing.set(false); 
          }
        }
      })
    } catch (error: any) {
      console.log('propose error', error);
      this.isProcessing.set(false);
      this.toastr.error(error);
    }    
  }

  /**
   * Determines whether method change on
   * @param methodKey 
   */
  onMethodChange(methodKey: any) {
    this.selectedMethod = this.methodList.find((m: any) => m.key === methodKey.target.value);
    console.log('selected method:', this.selectedMethod);
    if (this.selectedMethod.key === 'bulkDistribution') {
      this.getMonthlySummary();
    }
  }

  /**
   * Gets monthly summary
   */
  getMonthlySummary() {
    this.proposeService.getMonthlySummary().subscribe({
      next: (response: any) => {
        this.proposeForm.patchValue({
          count: response.data.total_docs,
          date: response.data.distribution_date
        })
      },
      error: (error: any) => {
        console.log('error', error);
      }
    })
  }

}

